import React, { useEffect, useState, useCallback, useMemo } from "react";
import Header from "./Header";
import { api } from "../server";

const typeColor = (t) =>
  ({
    info: "bg-info-subtle text-info",
    success: "bg-success-subtle text-success",
    warning: "bg-warning-subtle text-warning",
    error: "bg-danger-subtle text-danger",
    order: "bg-primary-subtle text-primary",
  })[t] || "bg-light text-dark";

const typeIcon = (t) =>
  ({
    info: "bi-info-circle",
    success: "bi-check2-circle",
    warning: "bi-exclamation-triangle",
    error: "bi-x-circle",
    order: "bi-cart",
  })[t] || "bi-bell";

function Notifications() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState("");
  const [type, setType] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userType) params.set("userType", userType);
      if (type) params.set("type", type);
      params.set("limit", "200");
      const data = await api.get(
        `/api/admin/notifications?${params.toString()}`,
      );
      setRows(data?.notifications || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userType, type]);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      unread: rows.filter((r) => !r.isRead).length,
      customers: rows.filter((r) => r.userType === "Customer").length,
      drivers: rows.filter((r) => r.userType === "Driver").length,
    }),
    [rows],
  );

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Notifications</h3>
                <div className="text-secondary lh-1">
                  All notifications sent to customers and drivers
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={() => {
                    setUserType("");
                    setType("");
                  }}
                >
                  <i className="bi bi-funnel me-2" />
                  Reset Filters
                </button>
                <button
                  type="button"
                  className="btn btn-light border px-4"
                  onClick={fetchRows}
                >
                  <i className="bi bi-arrow-clockwise me-2" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-bell" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Total</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-bell-fill" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Unread</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.unread}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-people" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Customers</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.customers}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-truck" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Drivers</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.drivers}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-funnel" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Filters</div>
                    <div className="text-secondary small">
                      Filter by recipient type or notification type
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <label className="form-label mb-1 fw-normal">
                      User Type
                    </label>
                    <select
                      className="form-select"
                      value={userType}
                      onChange={(e) => setUserType(e.target.value)}
                    >
                      <option value="">All User Types</option>
                      <option value="Customer">Customers</option>
                      <option value="Driver">Drivers</option>
                    </select>
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="form-label mb-1 fw-normal">
                      Notification Type
                    </label>
                    <select
                      className="form-select"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      <option value="">All Types</option>
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                      <option value="order">Order</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No notifications
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {rows.map((r, index) => (
                          <tr key={r._id}>
                            <td>{index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Title</span> &mdash;{" "}
                              <i
                                className={`bi ${typeIcon(r.type)} me-1 ${typeColor(r.type).split(" ")[1]}`}
                              />
                              {r.title}
                              <br />
                              <span className="text-dark">
                                Message
                              </span> &mdash;{" "}
                              <span className="text-secondary">
                                {r.message}
                              </span>
                              <br />
                              <span className="text-dark">User Type</span>{" "}
                              &mdash; {r.userType}
                              <br />
                              <span className="text-dark">
                                Type
                              </span> &mdash;{" "}
                              <span className={`badge ${typeColor(r.type)}`}>
                                {r.type}
                              </span>
                              <br />
                              <span className="text-dark">
                                Read
                              </span> &mdash;{" "}
                              {r.isRead ? (
                                <span className="text-secondary">Read</span>
                              ) : (
                                <span className="text-primary">Unread</span>
                              )}
                              <br />
                              <span className="text-dark">
                                Created
                              </span> &mdash;{" "}
                              <span className="text-secondary">
                                {new Date(r.createdAt).toLocaleString()}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 align-items-start">
                                {!r.isRead && (
                                  <span className="badge bg-primary-soft px-4 rounded-3 btn-py-sm">
                                    Unread
                                  </span>
                                )}
                                {r.isRead && (
                                  <span className="badge bg-secondary-soft px-4 rounded-3 btn-py-sm">
                                    Read
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default Notifications;
